import React, { useState, useEffect, useMemo, useCallback } from "react";
import Modal from "../../../../../components/ui/Modal";
import { fetchWithAuth } from "../../../../../utils/api";
import {
  swalConfirm,
  swalError,
  swalSuccess,
  swalWarning,
} from "../../../../../utils/swalAlert";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

export default function ScoreAdjustmentModal({
  isOpen,
  onClose,
  testId,
  isManualEntryTest = false,
  serverGradedCount = 0,
  data = [],
  student = null, // When provided, modal operates in SINGLE-STUDENT mode
  onApplied,
}) {
  const isSingleMode = Boolean(student);

  const [direction, setDirection] = useState("BOOST"); // "BOOST" | "DEDUCT"
  const [intensity, setIntensity] = useState(null); // null (unselected initially) | "MILD" | "HIGH"
  const [scoreModifierResults, setScoreModifierResults] = useState(null);
  const [isCheckingScore, setIsCheckingScore] = useState(false);
  const [selectedModifierRowIds, setSelectedModifierRowIds] = useState(new Set());
  const [isApplyingPolicy, setIsApplyingPolicy] = useState(false);
  const [modifierSearchQuery, setModifierSearchQuery] = useState("");
  const [modifierFilterType, setModifierFilterType] = useState("ALL"); // "ALL" | "CHANGED" | "UNCHANGED"

  // Reset state when modal opens or target changes
  useEffect(() => {
    if (isOpen) {
      setDirection("BOOST");
      setIntensity(null); // Never auto-select! User must explicitly choose.
      setScoreModifierResults(null);
      setSelectedModifierRowIds(new Set());
      setModifierSearchQuery("");
      setModifierFilterType("ALL");
    }
  }, [isOpen, student]);

  // Fetch all score IDs for graded students across the test (Bulk Mode)
  const fetchAllGradedScoreIds = useCallback(async () => {
    if (isManualEntryTest) {
      return data
        .filter((item) => item.status === "Graded" || item.scoreId)
        .map((item) => item.scoreId || item._id)
        .filter(Boolean);
    }

    try {
      const resp = await fetchWithAuth(
        `${serverUrl}/api/answer-paper/get-all/${testId}?all=true&minimal=true`,
        { method: "GET", credentials: "include" }
      );
      if (resp && resp.ok) {
        const raw = await resp.json();
        const list = Array.isArray(raw) ? raw : (raw.answerPapers || []);
        const gradedIds = list
          .filter((item) => item.status === "Graded" || item.scoreId)
          .map((item) => item.scoreId || item._id)
          .filter(Boolean);
        if (gradedIds.length > 0) return gradedIds;
      }
    } catch (err) {
      console.warn("Could not fetch answer papers for score adjustment:", err);
    }

    return data
      .filter((item) => item.status === "Graded" || item.scoreId)
      .map((item) => item.scoreId || item._id)
      .filter(Boolean);
  }, [data, isManualEntryTest, testId]);

  // Trigger calculation automatically when user picks direction or intensity
  const triggerCalculation = useCallback(
    async (dirToUse, intToUse) => {
      if (!dirToUse || !intToUse) return;

      const policyToUse = dirToUse === "BOOST" ? "Lenient" : "Strict";
      const thresholdToUse = intToUse === "MILD" ? 0.25 : 0.5;

      setIsCheckingScore(true);
      try {
        let scoreIds = [];
        if (isSingleMode) {
          const sId = student.scoreId || student._id;
          if (!sId) {
            swalWarning("No graded score record found for this student.");
            setIsCheckingScore(false);
            return;
          }
          scoreIds = [sId];
        } else {
          scoreIds = await fetchAllGradedScoreIds();
          if (!scoreIds || scoreIds.length === 0) {
            swalWarning("No graded student submissions found to adjust.");
            setIsCheckingScore(false);
            return;
          }
        }

        const response = await fetchWithAuth(
          `${serverUrl}/api/score/get-grading-policy-score`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              gradingPolicy: policyToUse,
              strategy: policyToUse,
              threshold: thresholdToUse,
              scoreIds,
            }),
          }
        );

        if (!response) {
          setIsCheckingScore(false);
          return;
        }

        const resData = await response.json();
        if (!response.ok || !resData.success) {
          throw new Error(resData.message || "Failed to calculate adjusted scores.");
        }

        const results = resData.data || resData.scores || [];
        setScoreModifierResults(results);

        // Auto-select all previewed students by default
        setSelectedModifierRowIds(
          new Set(results.map((item) => item.scoreId || item.answerPaperId).filter(Boolean))
        );
      } catch (error) {
        swalError(error.message || "Failed to preview score adjustments.");
      } finally {
        setIsCheckingScore(false);
      }
    },
    [fetchAllGradedScoreIds, isSingleMode, student]
  );

  // User changes direction (+ Give Extra Marks vs − Stricter Marking)
  const handleDirectionSelect = (newDir) => {
    setDirection(newDir);
    if (intensity) {
      triggerCalculation(newDir, intensity);
    }
  };

  // User selects an intensity card (Mild vs High) -> computes immediately!
  const handleIntensitySelect = (newInt) => {
    setIntensity(newInt);
    triggerCalculation(direction, newInt);
  };

  // Toggle individual student in preview table (Bulk Mode)
  const handleToggleModifierRow = (id) => {
    setSelectedModifierRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all or deselect all students (Bulk Mode)
  const handleSelectAllModifierRows = (shouldSelectAll) => {
    if (shouldSelectAll) {
      if (scoreModifierResults && scoreModifierResults.length > 0) {
        setSelectedModifierRowIds(
          new Set(scoreModifierResults.map((item) => item.scoreId || item.answerPaperId).filter(Boolean))
        );
      }
    } else {
      setSelectedModifierRowIds(new Set());
    }
  };

  // Apply changes to database (Single or Bulk)
  const handleApplyChanges = async () => {
    if (!scoreModifierResults || scoreModifierResults.length === 0) {
      swalWarning("No score adjustments to apply. Please select an option above.");
      return;
    }

    if (isSingleMode) {
      const sId = student.scoreId || student._id;
      const sInfo = scoreModifierResults[0] || {};
      const cScore = sInfo.currentScore ?? sInfo["current score"] ?? student.totalScore ?? 0;
      const nScore = sInfo.newScore ?? sInfo["new score"] ?? cScore;
      const sName = student.studentName || "this student";

      const confirmResult = await swalConfirm(
        "Apply Score Adjustment?",
        `Adjust score for ${sName} from ${Math.round(cScore * 100) / 100} to ${Math.round(nScore * 100) / 100}?`,
        "Yes, Save Score",
        "Cancel"
      );

      if (!confirmResult?.isConfirmed) return;

      setIsApplyingPolicy(true);
      try {
        const policyToUse = direction === "BOOST" ? "Lenient" : "Strict";
        const thresholdToUse = intensity === "MILD" ? 0.25 : 0.5;

        const response = await fetchWithAuth(
          `${serverUrl}/api/score/apply-grading-policy`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              policyName: policyToUse,
              gradingPolicy: policyToUse,
              threshold: thresholdToUse,
              scoreIds: [sId],
            }),
          }
        );

        if (!response || !response.ok) {
          throw new Error("Failed to save score adjustment.");
        }

        swalSuccess(`Score updated successfully for ${sName}!`);
        onClose();
        if (onApplied) {
          await onApplied();
        }
      } catch (err) {
        swalError(err.message || "Failed to update score.");
      } finally {
        setIsApplyingPolicy(false);
      }
    } else {
      // Bulk Mode
      if (selectedModifierRowIds.size === 0) {
        swalWarning("Please select at least one student to apply score adjustments.");
        return;
      }

      const selectedScoreIds = Array.from(selectedModifierRowIds);
      const symbolText =
        direction === "BOOST"
          ? intensity === "MILD"
            ? "Mild Boost (+)"
            : "High Boost (++)"
          : intensity === "MILD"
          ? "Mild Strict (−)"
          : "High Strict (−−)";

      const confirmResult = await swalConfirm(
        `Apply ${symbolText}?`,
        `Apply score adjustments to ${selectedScoreIds.length} selected student(s)? This will update their total scores for subjective questions.`,
        "Yes, Apply Adjustments",
        "Cancel"
      );

      if (!confirmResult?.isConfirmed) return;

      setIsApplyingPolicy(true);
      try {
        const policyToUse = direction === "BOOST" ? "Lenient" : "Strict";
        const thresholdToUse = intensity === "MILD" ? 0.25 : 0.5;

        const response = await fetchWithAuth(
          `${serverUrl}/api/score/apply-grading-policy`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              policyName: policyToUse,
              gradingPolicy: policyToUse,
              threshold: thresholdToUse,
              scoreIds: selectedScoreIds,
            }),
          }
        );

        if (!response) {
          setIsApplyingPolicy(false);
          return;
        }

        const resData = await response.json();
        if (!response.ok || !resData.success) {
          throw new Error(resData.message || "Failed to apply score adjustments.");
        }

        swalSuccess(
          resData.message || "Score adjustments applied and student scores updated successfully!"
        );

        onClose();
        if (onApplied) {
          await onApplied();
        }
      } catch (error) {
        swalError(error.message || "Failed to apply score adjustments.");
      } finally {
        setIsApplyingPolicy(false);
      }
    }
  };

  // Statistics calculation for Bulk Mode
  const modifierStats = useMemo(() => {
    if (!scoreModifierResults || scoreModifierResults.length === 0) {
      return { total: 0, changedCount: 0, unchangedCount: 0, avgDelta: 0 };
    }
    let changed = 0;
    let totalDelta = 0;
    scoreModifierResults.forEach((item) => {
      const cScore = item.currentScore ?? item["current score"] ?? 0;
      const nScore = item.newScore ?? item["new score"] ?? 0;
      const diff = nScore - cScore;
      if (Math.abs(diff) > 0.001) changed += 1;
      totalDelta += diff;
    });
    const avg =
      scoreModifierResults.length > 0
        ? Math.round((totalDelta / scoreModifierResults.length) * 100) / 100
        : 0;
    return {
      total: scoreModifierResults.length,
      changedCount: changed,
      unchangedCount: scoreModifierResults.length - changed,
      avgDelta: avg,
    };
  }, [scoreModifierResults]);

  // Filtered rows for the student table in Bulk Mode
  const filteredModifierRows = useMemo(() => {
    if (!scoreModifierResults) return [];
    return scoreModifierResults.filter((item) => {
      const sName = (item.studentName || item["student name"] || "").toLowerCase();
      const sRoll = (item.rollNo || item["roll no"] || item.rollNumber || "").toString().toLowerCase();
      const q = modifierSearchQuery.trim().toLowerCase();
      if (q && !sName.includes(q) && !sRoll.includes(q)) {
        return false;
      }
      const cScore = item.currentScore ?? item["current score"] ?? 0;
      const nScore = item.newScore ?? item["new score"] ?? 0;
      const isChanged = Math.abs(nScore - cScore) > 0.001;
      if (modifierFilterType === "CHANGED" && !isChanged) return false;
      if (modifierFilterType === "UNCHANGED" && isChanged) return false;
      return true;
    });
  }, [scoreModifierResults, modifierSearchQuery, modifierFilterType]);

  // Single student preview details
  const singleStudentPreview = useMemo(() => {
    if (!isSingleMode || !scoreModifierResults || scoreModifierResults.length === 0) {
      return null;
    }
    const item = scoreModifierResults[0];
    const cScore = item.currentScore ?? item["current score"] ?? student?.totalScore ?? 0;
    const nScore = item.newScore ?? item["new score"] ?? cScore;
    const diff = Math.round((nScore - cScore) * 100) / 100;
    return {
      currentScore: Math.round(cScore * 100) / 100,
      newScore: Math.round(nScore * 100) / 100,
      diff,
    };
  }, [isSingleMode, scoreModifierResults, student]);

  const modalTitle = isSingleMode
    ? `Adjust Score: ${student?.studentName || "Student"}`
    : "Adjust Scores";

  const modalSubtitle = isSingleMode
    ? [student?.rollNo && `Roll No: ${student.rollNo}`, student?.referenceId && `ID: ${student.referenceId}`]
        .filter(Boolean)
        .join(" • ") || "Adjust score on written answers for this student."
    : "Apply a score adjustment to written answers for all graded students in this test. Objective/MCQ scores are not changed.";

  const isSaveDisabled =
    intensity === null ||
    isCheckingScore ||
    isApplyingPolicy ||
    !scoreModifierResults ||
    scoreModifierResults.length === 0 ||
    (!isSingleMode && selectedModifierRowIds.size === 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth={isSingleMode ? "520px" : "780px"}>
      <div style={{ padding: "0.25rem", color: "#111827" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginBottom: "1.1rem",
            paddingBottom: "0.9rem",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "10px",
              background: "#fef3c7",
              color: "#d97706",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.2rem",
              flexShrink: 0,
            }}
          >
            <i className="fa-solid fa-sliders"></i>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <h2 style={{ fontSize: "1.25rem", fontWeight: "800", color: "#111827", margin: 0 }}>
                {modalTitle}
              </h2>
              <span
                style={{
                  fontSize: "0.72rem",
                  fontWeight: "600",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  background: "#eff6ff",
                  color: "#2563eb",
                  border: "1px solid #bfdbfe",
                }}
              >
                Written Questions Only
              </span>
            </div>
            <p style={{ margin: "3px 0 0", fontSize: "0.825rem", color: "#6b7280", lineHeight: 1.4 }}>
              {modalSubtitle}
            </p>
          </div>
        </div>

        {/* Step 1: Choose Adjustment Direction */}
        <div style={{ marginBottom: "1.25rem" }}>
          <div className="score-adj-section-title">1. Choose Adjustment Direction</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5rem",
              background: "#f3f4f6",
              padding: "4px",
              borderRadius: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => handleDirectionSelect("BOOST")}
              style={{
                padding: "0.65rem 0.5rem",
                border: "none",
                borderRadius: "6px",
                fontSize: "0.875rem",
                fontWeight: "700",
                cursor: "pointer",
                background: direction === "BOOST" ? "#ffffff" : "transparent",
                color: direction === "BOOST" ? "#16a34a" : "#4b5563",
                boxShadow: direction === "BOOST" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.45rem",
                transition: "all 0.15s ease",
              }}
            >
              <i className="fa-solid fa-arrow-trend-up"></i> + Give Extra Marks
            </button>
            <button
              type="button"
              onClick={() => handleDirectionSelect("DEDUCT")}
              style={{
                padding: "0.65rem 0.5rem",
                border: "none",
                borderRadius: "6px",
                fontSize: "0.875rem",
                fontWeight: "700",
                cursor: "pointer",
                background: direction === "DEDUCT" ? "#ffffff" : "transparent",
                color: direction === "DEDUCT" ? "#dc2626" : "#4b5563",
                boxShadow: direction === "DEDUCT" ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.45rem",
                transition: "all 0.15s ease",
              }}
            >
              <i className="fa-solid fa-scale-balanced"></i> − Stricter Marking
            </button>
          </div>
        </div>

        {/* Step 2: 2 Tactile Symbol Cards (Never preselected, computes instantly on click) */}
        <div style={{ marginBottom: "1.25rem" }}>
          <div className="score-adj-section-title">2. Choose Intensity</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            {/* Mild Card */}
            <div
              onClick={() => handleIntensitySelect("MILD")}
              role="button"
              tabIndex={0}
              style={{
                border: intensity === "MILD" ? "2px solid #f09a21" : "1px solid #e5e7eb",
                background: intensity === "MILD" ? "#fffdfa" : "#ffffff",
                boxShadow: intensity === "MILD" ? "0 1px 3px rgba(240, 154, 33, 0.15)" : "none",
                borderRadius: "10px",
                padding: "0.9rem",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.15s ease",
              }}
            >
              <div
                style={{
                  fontSize: "1.9rem",
                  fontWeight: "900",
                  color: direction === "BOOST" ? "#16a34a" : "#dc2626",
                  lineHeight: 1,
                  marginBottom: "0.3rem",
                }}
              >
                {direction === "BOOST" ? "+" : "−"}
              </div>
              <div style={{ fontWeight: "700", fontSize: "0.95rem", color: "#111827" }}>
                {direction === "BOOST" ? "Mild Boost (+)" : "Mild Strict (−)"}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "4px", lineHeight: 1.35 }}>
                {direction === "BOOST"
                  ? "Small score increase on written answers"
                  : "Small score deduction on written answers"}
              </div>
            </div>

            {/* High Card */}
            <div
              onClick={() => handleIntensitySelect("HIGH")}
              role="button"
              tabIndex={0}
              style={{
                border: intensity === "HIGH" ? "2px solid #f09a21" : "1px solid #e5e7eb",
                background: intensity === "HIGH" ? "#fffdfa" : "#ffffff",
                boxShadow: intensity === "HIGH" ? "0 1px 3px rgba(240, 154, 33, 0.15)" : "none",
                borderRadius: "10px",
                padding: "0.9rem",
                cursor: "pointer",
                textAlign: "center",
                transition: "all 0.15s ease",
              }}
            >
              <div
                style={{
                  fontSize: "1.9rem",
                  fontWeight: "900",
                  color: direction === "BOOST" ? "#16a34a" : "#dc2626",
                  lineHeight: 1,
                  marginBottom: "0.3rem",
                }}
              >
                {direction === "BOOST" ? "++" : "−−"}
              </div>
              <div style={{ fontWeight: "700", fontSize: "0.95rem", color: "#111827" }}>
                {direction === "BOOST" ? "High Boost (++)" : "High Strict (−−)"}
              </div>
              <div style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "4px", lineHeight: 1.35 }}>
                {direction === "BOOST"
                  ? "Bigger score increase on written answers"
                  : "Stricter score deduction on answers"}
              </div>
            </div>
          </div>
        </div>

        {/* State: Prompt User to Select Intensity */}
        {intensity === null && (
          <div
            style={{
              padding: "1.25rem",
              background: "#f9fafb",
              border: "1px dashed #d1d5db",
              borderRadius: "8px",
              textAlign: "center",
              color: "#6b7280",
              fontSize: "0.875rem",
              marginBottom: "1.25rem",
            }}
          >
            <i className="fa-solid fa-arrow-pointer" style={{ marginRight: "6px", color: "#f09a21" }}></i>
            Please select an intensity option above to preview the score change.
          </div>
        )}

        {/* State: Calculating Spinner */}
        {isCheckingScore && (
          <div
            style={{
              padding: "1.5rem",
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              textAlign: "center",
              color: "#4b5563",
              fontSize: "0.875rem",
              marginBottom: "1.25rem",
            }}
          >
            <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: "8px", color: "#f09a21" }}></i>
            Calculating preview...
          </div>
        )}

        {/* Single Student Score Preview Card */}
        {isSingleMode && !isCheckingScore && singleStudentPreview && (
          <div
            style={{
              marginBottom: "1.25rem",
              padding: "1rem",
              background: "#ffffff",
              borderRadius: "10px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                fontSize: "0.75rem",
                fontWeight: "700",
                color: "#6b7280",
                letterSpacing: "0.05em",
                marginBottom: "0.5rem",
              }}
            >
              SCORE PREVIEW
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <div>
                <span style={{ fontSize: "0.85rem", color: "#6b7280", marginRight: "6px" }}>Current Score:</span>
                <span style={{ fontSize: "1.25rem", fontWeight: "800", color: "#111827" }}>
                  {singleStudentPreview.currentScore}
                </span>
              </div>
              <i className="fa-solid fa-arrow-right" style={{ color: "#9ca3af", fontSize: "1rem" }}></i>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>New Score:</span>
                <span
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "800",
                    color: direction === "BOOST" ? "#16a34a" : "#dc2626",
                  }}
                >
                  {singleStudentPreview.newScore}
                </span>
                {singleStudentPreview.diff !== 0 && (
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: "700",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      background: singleStudentPreview.diff > 0 ? "#ecfdf5" : "#fef2f2",
                      color: singleStudentPreview.diff > 0 ? "#16a34a" : "#dc2626",
                    }}
                  >
                    {singleStudentPreview.diff > 0 ? `+${singleStudentPreview.diff}` : singleStudentPreview.diff}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Bulk Mode: KPIs & Directly Visible Student List (No Accordion / No Collapse) */}
        {!isSingleMode && !isCheckingScore && scoreModifierResults && (
          <div style={{ marginTop: "1rem" }}>
            <div className="score-adj-section-title">3. Review Summary & Students</div>

            {/* KPI Summary Cards */}
            <div className="score-adj-kpi-bar">
              <div className="score-adj-kpi-card">
                <div className="score-adj-kpi-val">{modifierStats.total}</div>
                <div className="score-adj-kpi-lbl">Graded Students</div>
              </div>
              <div className="score-adj-kpi-card">
                <div className="score-adj-kpi-val" style={{ color: "#16a34a" }}>
                  {modifierStats.changedCount}
                </div>
                <div className="score-adj-kpi-lbl">Scores Changed</div>
              </div>
              <div className="score-adj-kpi-card">
                <div
                  className="score-adj-kpi-val"
                  style={{ color: modifierStats.avgDelta >= 0 ? "#16a34a" : "#dc2626" }}
                >
                  {modifierStats.avgDelta > 0 ? `+${modifierStats.avgDelta}` : modifierStats.avgDelta}
                </div>
                <div className="score-adj-kpi-lbl">Avg Score Delta</div>
              </div>
              <div className="score-adj-kpi-card">
                <div className="score-adj-kpi-val" style={{ color: "#d97706" }}>
                  {selectedModifierRowIds.size}
                </div>
                <div className="score-adj-kpi-lbl">Selected to Apply</div>
              </div>
            </div>

            {/* Direct Student Table Container (Always Visible) */}
            <div
              style={{
                marginTop: "0.85rem",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "0.75rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "0.65rem",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <span style={{ fontSize: "0.825rem", color: "#334155", fontWeight: "600" }}>
                  <i className="fa-solid fa-circle-check" style={{ color: "#16a34a", marginRight: "6px" }}></i>
                  All {selectedModifierRowIds.size} students are selected for this score adjustment. Uncheck any student to exclude them.
                </span>
              </div>

              {/* Filter and Search Toolbar */}
              <div className="score-adj-filter-strip">
                <div style={{ position: "relative", flex: 1, minWidth: "160px" }}>
                  <i
                    className="fa-solid fa-magnifying-glass"
                    style={{
                      position: "absolute",
                      left: "0.65rem",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#9ca3af",
                      fontSize: "0.78rem",
                    }}
                  />
                  <input
                    type="text"
                    className="score-adj-search"
                    placeholder="Search student or roll no..."
                    value={modifierSearchQuery}
                    onChange={(e) => setModifierSearchQuery(e.target.value)}
                  />
                </div>

                {/* Filter Tabs */}
                <div className="score-adj-tabs">
                  <button
                    type="button"
                    className={`score-adj-tab-btn ${modifierFilterType === "ALL" ? "active" : ""}`}
                    onClick={() => setModifierFilterType("ALL")}
                  >
                    All ({modifierStats.total})
                  </button>
                  <button
                    type="button"
                    className={`score-adj-tab-btn ${modifierFilterType === "CHANGED" ? "active" : ""}`}
                    onClick={() => setModifierFilterType("CHANGED")}
                  >
                    Changed ({modifierStats.changedCount})
                  </button>
                  <button
                    type="button"
                    className={`score-adj-tab-btn ${modifierFilterType === "UNCHANGED" ? "active" : ""}`}
                    onClick={() => setModifierFilterType("UNCHANGED")}
                  >
                    Unchanged ({modifierStats.unchangedCount})
                  </button>
                </div>

                {/* Batch Selection Buttons */}
                <div style={{ display: "flex", gap: "0.4rem" }}>
                  <button
                    type="button"
                    onClick={() => handleSelectAllModifierRows(true)}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #d1d5db",
                      borderRadius: "5px",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSelectAllModifierRows(false)}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #d1d5db",
                      borderRadius: "5px",
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      fontWeight: "600",
                      color: "#374151",
                    }}
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              {/* Direct Table */}
              <div
                style={{
                  maxHeight: "280px",
                  overflowY: "auto",
                  border: "1px solid #e5e7eb",
                  borderRadius: "6px",
                  background: "#ffffff",
                  marginTop: "0.5rem",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.825rem",
                    textAlign: "left",
                  }}
                >
                  <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "#f8fafc" }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      <th style={{ width: "40px", padding: "8px 10px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={
                            scoreModifierResults.length > 0 &&
                            selectedModifierRowIds.size === scoreModifierResults.length
                          }
                          onChange={(e) => handleSelectAllModifierRows(e.target.checked)}
                          aria-label="Select or deselect all students"
                        />
                      </th>
                      <th style={{ padding: "8px 10px", color: "#475569", fontWeight: "700" }}>Student</th>
                      <th style={{ padding: "8px 10px", color: "#475569", fontWeight: "700", textAlign: "center" }}>
                        Current Score
                      </th>
                      <th style={{ padding: "8px 10px", color: "#475569", fontWeight: "700", textAlign: "center" }}>
                        New Score
                      </th>
                      <th style={{ padding: "8px 10px", color: "#475569", fontWeight: "700", textAlign: "center" }}>
                        Change
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredModifierRows.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ padding: "18px", textAlign: "center", color: "#64748b" }}>
                          No students match the current search or filter.
                        </td>
                      </tr>
                    ) : (
                      filteredModifierRows.map((item, idx) => {
                        const rowKey = item.scoreId || item.answerPaperId;
                        const isSelected = selectedModifierRowIds.has(rowKey);
                        const sName = item.studentName || item["student name"] || "Unknown";
                        const sRoll = item.rollNo || item["roll no"] || item.rollNumber;
                        const cScore = item.currentScore ?? item["current score"] ?? 0;
                        const nScore = item.newScore ?? item["new score"] ?? 0;
                        const diff = Math.round((nScore - cScore) * 100) / 100;

                        return (
                          <tr
                            key={rowKey || idx}
                            onClick={() => handleToggleModifierRow(rowKey)}
                            style={{
                              borderBottom: idx !== filteredModifierRows.length - 1 ? "1px solid #f1f5f9" : "none",
                              background: isSelected ? "#fffbeb" : idx % 2 === 0 ? "#ffffff" : "#fcfcfc",
                              cursor: "pointer",
                              transition: "background-color 0.12s ease",
                            }}
                          >
                            <td
                              style={{ width: "40px", padding: "8px 10px", textAlign: "center" }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleModifierRow(rowKey)}
                              />
                            </td>
                            <td style={{ padding: "8px 10px" }}>
                              <div style={{ fontWeight: "600", color: "#1e293b" }}>{sName}</div>
                              {sRoll && (
                                <div style={{ fontSize: "0.72rem", color: "#64748b" }}>Roll No: {sRoll}</div>
                              )}
                            </td>
                            <td style={{ padding: "8px 10px", textAlign: "center", color: "#475569", fontWeight: "600" }}>
                              {cScore}
                            </td>
                            <td style={{ padding: "8px 10px", textAlign: "center", fontWeight: "700", color: "#0f172a" }}>
                              {nScore}
                            </td>
                            <td style={{ padding: "8px 10px", textAlign: "center" }}>
                              {diff > 0 && (
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontSize: "0.75rem",
                                    fontWeight: "700",
                                    color: "#16a34a",
                                    background: "#dcfce7",
                                  }}
                                >
                                  +{diff}
                                </span>
                              )}
                              {diff < 0 && (
                                <span
                                  style={{
                                    display: "inline-block",
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    fontSize: "0.75rem",
                                    fontWeight: "700",
                                    color: "#dc2626",
                                    background: "#fee2e2",
                                  }}
                                >
                                  {diff}
                                </span>
                              )}
                              {diff === 0 && (
                                <span style={{ color: "#94a3b8", fontSize: "0.75rem" }}>0</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Footer / Bottom Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "1.25rem",
            paddingTop: "0.85rem",
            borderTop: "1px solid #e5e7eb",
            flexWrap: "wrap",
            gap: "0.5rem",
          }}
        >
          <div style={{ fontSize: "0.825rem", color: "#6b7280" }}>
            {!isSingleMode && scoreModifierResults && (
              <span>
                {selectedModifierRowIds.size} of {scoreModifierResults.length} student
                {scoreModifierResults.length === 1 ? "" : "s"} selected
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isApplyingPolicy}
              style={{
                padding: "0.55rem 1rem",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                background: "#ffffff",
                color: "#374151",
                fontWeight: "600",
                fontSize: "0.875rem",
                cursor: isApplyingPolicy ? "not-allowed" : "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleApplyChanges}
              disabled={isSaveDisabled}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem",
                padding: "0.55rem 1.25rem",
                borderRadius: "6px",
                background: isSaveDisabled ? "#e5e7eb" : "#f09a21",
                color: isSaveDisabled ? "#9ca3af" : "#ffffff",
                border: "none",
                fontWeight: "700",
                fontSize: "0.875rem",
                cursor: isSaveDisabled ? "not-allowed" : "pointer",
                boxShadow: isSaveDisabled ? "none" : "0 1px 2px rgba(0,0,0,0.06)",
                transition: "all 0.15s ease",
              }}
            >
              {isApplyingPolicy ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i> Saving...
                </>
              ) : isSingleMode ? (
                <>
                  <i className="fa-solid fa-check"></i> Save Adjusted Score
                </>
              ) : (
                <>
                  <i className="fa-solid fa-check"></i> Apply Adjustments ({selectedModifierRowIds.size})
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
